<script>
import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import { Freight } from "@/services/westore/entities";
import FormSkeleton from "../../../components/skeletons/form-skeleton.vue";
import moment from "moment";

export default {
  page: {
    title: "Freight Details",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    FormSkeleton,
  },
  data() {
    return {
      title: this.$t("Freight Details"),
      itemDetails: {},
      containers: [],
      flights: [],
      loaded: false,
      showContentLoader: false,
      showModal: false,
      currentAttachments: []
    };
  },
  async created() {
    if (!this.rowData) {
      await this.fetchDetails();
    } else {
      this.itemDetails = this.rowData;
    }
  },
  computed: {
    hasAttachments() {
      const details = this.itemDetails;
      return details.customer_quotation_attachment || details.agent_quotation_attachment ||
        details.debit_note_attachment || details.mbl_attachment || details.hbl_attachment ||
        details.consignee_amendment_attachment || details.awb_attachment ||
        (details.agent_correspondence_attachment && details.agent_correspondence_attachment.length) ||
        (details.customer_correspondence_attachment && details.customer_correspondence_attachment.length) ||
        (details.shipper_attachment && details.shipper_attachment.length) ||
        (details.bayan_attachment && details.bayan_attachment.length) ||
        (details.debit_note_attachment && details.debit_note_attachment.length);
    }
  },
  methods: {
    async fetchDetails() {
      this.loaded = false;
      this.showContentLoader = true;
      await Freight.getOne(this.$route.params?.id)
        .then((res) => {
          this.itemDetails = res.data;
          this.containers = res.data.containers;
          this.flights = res.data.flights;
        })
        .finally(() => {
          this.loaded = true;
          this.showContentLoader = false;
        });
    },
    checkNull(value) {
      return value || value === 0 ? value : "NA";
    },
    convertDate: function (timestampValue) {
      return moment
        .unix(timestampValue)
        .utcOffset("+03:00")
        .format("DD-MM-YYYY/hh:mm:ss([GMT]Z)");
    },
    //handle attachment links
    getAttachmentLink(attachments) {
      if (!attachments) return this.checkNull(attachments);
      const attachmentArray = Array.isArray(attachments) ? attachments : [attachments];
      return attachmentArray.map(attachment => {
        const url = typeof attachment === 'object' && attachment.url ? attachment.url : attachment;
        if (typeof url === 'string') {
          const fileName = url.split('/').pop();
          return `<a href="#" @click.prevent="openModal('${url}')" style="color:#e33065;">${fileName}</a>`;
        }
        return '';
      }).join(', ');
    },
    openModal(type) {
      this.showModal = true;
      switch (type) {
        case 'shipper':
          this.currentAttachments = this.itemDetails.shipper_attachment || [];
          break;
        case 'bayan':
          this.currentAttachments = this.itemDetails.bayan_attachment || [];
          break;
        case 'debitNote':
          this.currentAttachments = this.itemDetails.debit_note_attachment || [];
          break;
        case 'agent_correspondence':
          this.currentAttachments = this.itemDetails.agent_correspondence_attachment || [];
          break;
        case 'customer_correspondence':
          this.currentAttachments = this.itemDetails.customer_correspondence_attachment || [];
          break;
        default:
          this.currentAttachments = [];
      }
    },
    closeModal() {
      this.showModal = false;
      this.currentAttachments = [];
    }
  },
};
</script>
<template>
  <Layout>
    <PageHeader :title="title" class="d-print-none"></PageHeader>
    <div class="pt-5">
      <FormSkeleton v-if="showContentLoader" />
      <v-spacer />
      <div class="card">
        <div v-if="showContentLoader === false && loaded && itemDetails !== null" class="card-body">
          <h4 class="card-title mb-4 p-2">
            <span>
              {{ $t("Order") }}
              <code class="pink--text">{{ itemDetails.id }}</code>
            </span>
          </h4>

          <!-- Details -->
          <div class="table-responsive">
            <table class="table table-nowrap mb-0">
              <tbody>
                <tr>
                  <th scope="row">{{ $t("Customer Name") }}</th>
                  <td>{{ checkNull(itemDetails.merchant.name) }}</td>
                  <th scope="row">{{ $t("Created At") }}</th>
                  <td>{{ convertDate(itemDetails.created) }}</td>
                </tr>
                <tr>
                  <th scope="row">{{ $t("Status") }}</th>
                  <td>
                    <span class="d-flex align-items-center p-1 rounded-1 text-white" style="width: fit-content;"
                      :class="itemDetails.status == 'Arrived' ? 'delivered-badge' : 'on-hold-badge'">
                      {{ checkNull(itemDetails.status) }}
                    </span>
                  </td>
                  <th scope="row">{{ $t("Customer reference") }}</th>
                  <td>{{ checkNull(itemDetails.customer_reference) }}</td>
                </tr>
                <tr>
                  <th v-if="itemDetails.type.text == 'Sea'" scope="row">{{ $t("MBL") }}</th>
                  <th v-else scope="row">{{ $t("Tracking") }}</th>
                  <td>#{{ checkNull(itemDetails.ref_number) }}</td>
                  <th v-if="itemDetails.type.text == 'Sea'" scope="row">{{ $t("HBL") }}</th>
                  <td v-if="itemDetails.type.text == 'Sea'">{{ checkNull(itemDetails.hbl) }}</td>
                </tr>
                <tr>
                  <th scope="row">{{ $t("Type") }}</th>
                  <td>{{ checkNull(itemDetails.type.text) }}</td>
                  <th v-if="itemDetails.type.text === 'Air' || itemDetails.type.text === 'Sea'" scope="row">{{
                    $t("Shipping Company") }}</th>
                  <td>{{ checkNull(itemDetails.carrier_code) }}</td>
                </tr>
                <tr v-if="itemDetails.type.text === 'Air'">
                  <th scope="row">{{ $t("Origin") }}</th>
                  <td>{{ checkNull(itemDetails.origin) }}</td>
                  <th scope="row">{{ $t("Destination") }}</th>
                  <td>{{ checkNull(itemDetails.destination) }}</td>
                </tr>
                <tr v-if="itemDetails.type.text === 'Air'">
                  <th scope="row">{{ $t("Chargable Weight") }}</th>
                  <td>{{ checkNull(itemDetails.chargable_weight) }}</td>
                </tr>
                <tr v-if="itemDetails.type.text === 'Land'">
                  <th scope="row">{{ $t("Shipper") }}</th>
                  <td>{{ checkNull(itemDetails.shipper && itemDetails.shipper.name) }}</td>
                  <th scope="row">{{ $t("Master Origin") }}</th>
                  <td>{{ checkNull(itemDetails.master_origin && itemDetails.master_origin.name) }}</td>
                </tr>
                <tr>
                  <th scope="row">{{ $t("Master Destination") }}</th>
                  <td>{{ checkNull(itemDetails.master_destination && itemDetails.master_destination.name) }}</td>

                  <th scope="row">{{ $t("Master Origin") }}</th>
                  <td>{{ checkNull(itemDetails.master_origin && itemDetails.master_origin.name) }}</td>
                </tr>
                <tr>
                  <th scope="row">{{ $t("Number of trucks") }}</th>
                  <td>{{ checkNull(itemDetails.trucks_count) }}</td>
                  <th scope="row">{{ $t("Number of packages") }}</th>
                  <td>{{ checkNull(itemDetails.packages_count) }}</td>
                </tr>
                <tr>
                  <th scope="row">{{ $t("Bayan") }}</th>
                  <td>{{ checkNull(itemDetails.bayan) }}</td>
                  <th scope="row">{{ $t("Weight in KG") }}</th>
                  <td>{{ checkNull(itemDetails.weight_kg) }}</td>
                </tr>
                <tr>
                  <th scope="row">{{ $t("Cargo description") }}</th>
                  <td>{{ checkNull(itemDetails.cargo_description) }}</td>
                  <th scope="row">{{ $t("Truck WB") }}</th>
                  <td>{{ checkNull(itemDetails.truck_wb) }}</td>
                </tr>
                <!---------Attachments--------->
                <h4 v-if="hasAttachments" class="text-secondary mx-3 my-3">Attachments</h4>
                <!--Shared-Attach-->
                <tr
                  v-if="(itemDetails.type.text === 'Air' || itemDetails.type.text === 'Sea') && itemDetails.customer_quotation_attachment">
                  <th scope="row">{{ $t('Customer Quotation') }}</th>
                  <td v-html="getAttachmentLink(itemDetails.customer_quotation_attachment)"></td>
                </tr>

                <tr
                  v-if="(itemDetails.type.text === 'Air' || itemDetails.type.text === 'Sea') && itemDetails.agent_quotation_attachment">
                  <th scope="row">{{ $t('Agent Quotation') }}</th>
                  <td v-html="getAttachmentLink(itemDetails.agent_quotation_attachment)"></td>
                </tr>

                <tr
                  v-if="(itemDetails.type.text === 'Air' || itemDetails.type.text === 'Sea') && itemDetails.debit_note_attachment">
                  <th scope="row">{{ $t('Debit Note') }}</th>
                  <td v-html="getAttachmentLink(itemDetails.debit_note_attachment)"></td>
                </tr>

                <!--Sea-Attach-->
                <tr v-if="itemDetails.type.text === 'Sea' && itemDetails.mbl_attachment">
                  <th scope="row">{{ $t('MBL Attachment') }}</th>
                  <td v-html="getAttachmentLink(itemDetails.mbl_attachment)"></td>
                </tr>

                <tr v-if="itemDetails.type.text === 'Sea' && itemDetails.hbl_attachment">
                  <th scope="row">{{ $t('HBL Attachment') }}</th>
                  <td v-html="getAttachmentLink(itemDetails.hbl_attachment)"></td>
                </tr>

                <tr v-if="itemDetails.type.text === 'Sea' && itemDetails.consignee_amendment_attachment">
                  <th scope="row">{{ $t('Consignee Amendment Attachment') }}</th>
                  <td v-html="getAttachmentLink(itemDetails.consignee_amendment_attachment)"></td>
                </tr>

                <!--Air-Attach-->
                <tr v-if="itemDetails.type.text === 'Air' && itemDetails.awb_attachment">
                  <th scope="row">{{ $t('AWB Attachment') }}</th>
                  <td v-html="getAttachmentLink(itemDetails.awb_attachment)"></td>
                </tr>

                <!--Land-Attach-->
                <tr v-if="itemDetails.type.text === 'Land' && itemDetails.awb_attachment">
                  <th scope="row">{{ $t('AWB Attachment') }}</th>
                  <td v-html="getAttachmentLink(itemDetails.awb_attachment)"></td>
                </tr>

                <!--Shared-Attach-->
                <tr
                  v-if="itemDetails.agent_correspondence_attachment && itemDetails.agent_correspondence_attachment.length">
                  <th scope="row">{{ $t('Agent Correspondence') }}</th>
                  <td>
                    <button @click="openModal('agent_correspondence')" style="color:#e33065;">
                      View Attachments ({{ itemDetails.agent_correspondence_attachment.length }})
                    </button>
                  </td>
                </tr>

                <tr
                  v-if="itemDetails.customer_correspondence_attachment && itemDetails.customer_correspondence_attachment.length">
                  <th scope="row">{{ $t('Customer Correspondence') }}</th>
                  <td>
                    <button @click="openModal('customer_correspondence')" style="color:#e33065;">
                      View Attachments ({{ itemDetails.customer_correspondence_attachment.length }})
                    </button>
                  </td>
                </tr>

                <!--Multi-Attachment-->
                <tr v-if="itemDetails.shipper_attachment && itemDetails.shipper_attachment.length">
                  <th scope="row">{{ $t('Shipper Attachments') }}</th>
                  <td>
                    <button @click="openModal('shipper')" style="color:#e33065;">
                      View Attachments ({{ itemDetails.shipper_attachment.length }})
                    </button>
                  </td>
                </tr>

                <tr v-if="itemDetails.bayan_attachment && itemDetails.bayan_attachment.length">
                  <th scope="row">{{ $t('Byan Attachments') }}</th>
                  <td>
                    <button @click="openModal('bayan')" style="color:#e33065;">
                      View Attachments ({{ itemDetails.bayan_attachment.length }})
                    </button>
                  </td>
                </tr>

                <tr v-if="itemDetails.debit_note_attachment && itemDetails.debit_note_attachment.length">
                  <th scope="row">{{ $t('Debit Note Attachments') }}</th>
                  <td>
                    <button @click="openModal('debitNote')" style="color:#e33065;">
                      View Attachments ({{ itemDetails.debit_note_attachment.length }})
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!--Flights-Details-->
            <div v-if="itemDetails.type.text == 'Air'">
              <div class="flightInfo text-primary mt-10">
                <h4 class="text-primary">Flight Info</h4>
                <div v-if="flights.length == 0">
                  <h5 class="text-secondary text-center p-5">
                    There is No Flights
                  </h5>
                </div>
                <div v-else>
                  <div v-for="item in flights" :key="item.id" class="flight-wrapper">
                    <p>Flight: {{ item.number }}</p>
                    <div>
                      <p>Ship From: {{ item.shipment_from }}</p>
                      <hr />
                      <p>Ship To: {{ item.shipment_to }}</p>
                    </div>
                    <div>
                      <p>
                        Expected departure time:
                        <span v-if="item.etd != null">{{
                          convertDate(item.etd)
                        }}</span><span v-else>Not Avilable</span>
                      </p>
                      <p>
                        Expected arrival time:
                        <span v-if="item.eta != null">{{
                          convertDate(item.eta)
                        }}</span><span v-else>Not Avilable</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!--Containers-->
            <div v-for="item in containers" :key="item.id" class="mt-10">
              <div v-if="itemDetails.type.text == 'Sea'" class="d-flex align-center justify-content-between mb-4">
                <div class="text-primary fw-bold font-size-14 d-flex flex-column">
                  <p>Container: #{{ checkNull(item.container_number) }}</p>
                  <p>
                    Expected departure time:
                    <span v-if="item.estimated_depart_time != null">
                      {{ convertDate(item.estimated_depart_time) }}</span>
                    <span v-else>Not Available</span>
                  </p>

                </div>

                <div class="text-primary fw-bold font-size-14 d-flex flex-column">
                  <p v-if="item.type != null">Type: {{ checkNull(item.type.text) }}</p>
                  <p>
                    Expected arrival time:
                    <span v-if="item.estimated_arrive_time != null">
                      {{ convertDate(item.estimated_arrive_time) }}</span>
                    <span v-else>Not Available</span>
                  </p>
                </div>
              </div>
              <h4 class="mt-5 text-primary" v-if="itemDetails.type.text == 'Air'">
                Track Details
              </h4>
              <h5 v-if="item.history.length === 0" class="text-secondary text-center p-5">
                There is No Tracking Details

              </h5>
              <div v-else class="border border-1 border-grey p-2 rounded">
                <div v-for="history in item.history" class="log-box d-flex flex-column rounded mb-3" :key="history.id">
                  <div class="grow d-flex align-center justify-content-between py-2 px-4">
                    <div class="mx-2 font-size-12">
                      <span class="text-primary">Event time: </span>
                      <span class="text-secondary">{{
                        checkNull(convertDate(history.event_time))
                      }}</span>
                    </div>
                    <div class="border-top grow secondary"></div>
                    <div class="mx-2 font-size-12">
                      <span class="text-primary">Status Update: </span>
                      <span class="text-info">{{
                        checkNull(history.details)
                      }}</span>
                    </div>

                    <div class="border-top grow secondary"></div>
                    <div v-if="itemDetails.type.text == 'Sea'" class="mx-2 font-size-12">
                      <span class="text-primary">Vessel name: </span>
                      <span class="text-info">{{
                        checkNull(history.vessel_name)
                      }}</span>
                    </div>

                    <div class="border-top grow secondary"></div>
                    <div v-if="itemDetails.type.text == 'Sea'" class="mx-2 font-size-12">
                      <span class="text-primary">Location: </span>
                      <span class="text-info">{{
                        checkNull(history.location_name)
                      }}</span>
                    </div>

                    <div v-if="itemDetails.type.text == 'Air'" class="mx-2 font-size-12">
                      <span class="text-primary">Flight number: </span>
                      <span class="text-info">{{
                        checkNull(history.flight_number)
                      }}</span>
                    </div>
                    <!-- <div class="border-top grow secondary"></div>
                     <div class="mx-2 font-size-12">
                      <span class="text-primary">Station: </span>
                      <span class="text-info">{{ history.station }}</span>
                    </div>   -->
                    <div v-if="itemDetails.type.text == 'Air'" class="border-top grow secondary"></div>
                    <div v-if="itemDetails.type.text == 'Air'" class="mx-2 font-size-12">
                      <span class="text-primary">Pieces: </span>
                      <span class="text-info">{{
                        checkNull(history.pieces)
                      }}</span>
                    </div>
                    <div v-if="itemDetails.type.text == 'Air'" class="border-top grow secondary"></div>
                    <div v-if="itemDetails.type.text == 'Air'" class="mx-2 font-size-12">
                      <span class="text-primary">Weight: </span>
                      <span class="text-info">{{
                        checkNull(history.weight)
                      }}</span>
                    </div>
                  </div>
                  <div class="mx-2 font-size-12 px-4 py-2">
                    <span class="text-primary">Event Details: </span>
                    <span class="text-info">{{
                      checkNull(history.details)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal for Previewing Attachments -->
        <div :class="['modal-overlay', { 'is-active': showModal }]" @click.self="closeModal">
          <div class="modal-content">
            <button class="close-button" @click="closeModal">X</button>
            <h4 class="text-center mb-5">Attachments</h4>
            <ul>
              <li class="mb-3" v-for="attachment in currentAttachments" :key="attachment.id">
                <a :href="attachment.url" target="_blank" style="color:#e33065;">
                  {{ attachment.url.split('/').pop() }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.modal-overlay.is-active {
  opacity: 1;
  pointer-events: auto;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  width: 35%;
  transform: translateY(-20px);
  transition: transform 0.3s ease, opacity 0.3s ease;
  opacity: 0;

  ul {
    li {
      a {
        color: #e33065 !important;
        transition: 0.4s ease all;

        &:hover {
          color: #101820 !important;
        }
      }
    }
  }
}

.modal-overlay.is-active .modal-content {
  opacity: 1;
  transform: translateY(0);
}


.close-button {
  color: rgb(194, 194, 194);
  text-align: right;
  border: none;
  cursor: pointer;
  font-weight: 800;
  transition: 0.4s ease all;

  &:hover {
    color: red;
  }
}


table {
  tr {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.table-nowrap {

  td,
  th {
    white-space: nowrap;
    background-color: white;
    border-bottom: 0;
    flex: 1;
  }
}

.log-box {
  background-color: #eff1f2;
}

.air-type {
  tr {
    background: transparent;
    border: none;
  }
}

//Flight-Details
.flight-wrapper {
  margin: 35px auto;
  width: 70%;

  hr {
    border: none;
    border-top: 1px solid #e33065;
    width: 45%;
    margin: 0 10px;
    opacity: 1;
  }

  p {
    text-align: center;
    margin: 3px 0;
    font-size: 15px;
  }

  &>div {
    display: flex;
    justify-content: space-between;
  }

  &>div:first-of-type {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
